<template>
    <RegularModal ref="modal">
        <!-- Title -->
        <template v-slot:title>
            <span>{{ translate("federation.migrate_event_to_federation") }}</span>
        </template>

        <!-- Body -->
        <template v-slot:body>
            <div v-if="!initDone" class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <div v-else-if="eventData">
                <!-- Event Summary -->
                <div class="box is-shadowless p-3 mb-4">
                    <div class="is-flex is-align-items-center">
                        <figure v-if="eventData.event.thumbnailImage" class="image is-64x64 mr-3">
                            <img :src="eventData.event.thumbnailImage" />
                        </figure>
                        <div>
                            <p class="is-size-5 mb-1">{{ eventData.event.fullName }}</p>
                            <p class="has-text-grey-light">
                                {{ eventData.event.place }} •
                                {{ convertToTimezone(eventData.event.eventDate.startDate, eventData.event.timezone, "yyyy-MM-dd") }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Federation Selection -->
                <div class="field">
                    <label class="label">{{ translate("federation_select.select_federation") }}:</label>
                    <FederationSelect
                        :dropdown-mode="'container'"
                        v-model="selectedFederationId"
                        :remember-selection-key="'migrationModalSelectedFederation'"
                    />
                </div>
            </div>
        </template>

        <!-- Footer -->
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton
                    :is-loading="confirmInProgress"
                    class="button is-primary"
                    :disabled="!selectedFederationId"
                    @click="confirmMigration"
                >
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import { useModal } from "../Common/useModal";
    import events from "../Api/events";
    import federationsApi from "../Api/federationsApi";
    import { FullEvent } from "../Common/types";
    import FederationSelect from "../Federations/Common/FederationSelect.vue";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { translate } from "../Common/translations"; // adjust import

    const { modal, open: openModal, close } = useModal();

    const eventData = ref<FullEvent | null>(null);
    const selectedEventId = ref<number | null>(null);

    const selectedFederationId = ref<string>("");

    const initDone = ref(false);

    function open(eventId: number) {
        initDone.value = false;
        selectedEventId.value = eventId;
        eventData.value = null;
        selectedFederationId.value = "";

        openModal();

        return events
            .getAdminEvent(eventId)
            .then((data: FullEvent) => {
                eventData.value = data;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    const confirmInProgress = ref(false);

    const emit = defineEmits(["migrated"]);

    function confirmMigration() {
        confirmInProgress.value = true;
        return federationsApi
            .migrateEventToFederation({
                eventId: Number(selectedEventId.value),
                federationId: selectedFederationId.value,
            })
            .then(() => {
                successToast(translate("federation.event_migrated"));
                emit("migrated");
                close();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
